import InputField from "components/fields/InputField";
import Button from "components/button";
import LoaderButton from "components/button/LoaderButton";
import SelectField from "components/fields/SelectField";
import Card from "components/card";

import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { AddNewOrder, getUsersList } from "store/orderSlice";
import AddItemModal from "./AddItemsModal";
import { createColumnHelper } from "@tanstack/react-table";
import WithoutPageTable from "components/table/WithoutPageTable";
import { FaTrashAlt } from "react-icons/fa";
import { FiPlus } from "react-icons/fi";
import { ORDER_STATUS } from "constants";
import { showToast } from "components/toast";
import { ORDER_TYPE } from "constants";
import { SHIPMENT_TYPE } from "constants";
import { useNavigate } from "react-router-dom";
import Radio from "components/radio";
import SearchSelectField from "components/fields/SearchSelectField";
const columnHelper = createColumnHelper();

function CreateOrder() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { order: { draft = {} } = {} } = useSelector(
    (state) => state.auth.permissions || {}
  );
  const { createLoading, userListData, orderStatusData } = useSelector(
    (state) => state.orders
  );

  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    dispatch(getUsersList({ page: 1, limit: 10, search: searchText }));
  }, [dispatch, searchText]);

  const orderForOptions = useMemo(() => {
    if (!Array.isArray(userListData)) return [];
    return userListData.map((o) => ({
      value: o._id,
      label: `${o.firstname} ${o.lastname}`,
    }));
  }, [userListData]);

  const { draftOrderId } = useMemo(() => {
    if (!orderStatusData) return { draftOrderId: null };

    const draftItem = orderStatusData.find(
      (item) => item.enumVal === ORDER_STATUS.DRAFT
    );

    return {
      draftOrderId: draftItem?._id || null,
    };
  }, [orderStatusData]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      orderType: ORDER_TYPE.BASED_ON_DONE,
      shipmentType: SHIPMENT_TYPE.BY_AIR,
      ...(draft.selectUser ? { orderFor: "" } : {}),
      description: "",
      items: [],
    },
  });

  const { append, remove, replace } = useFieldArray({
    control,
    name: "items",
  });

  const items = watch("items") ?? [];
  const orderType = watch("orderType"); // 👈 watch mode

  useEffect(() => {
    replace([]);
  }, [orderType, replace]);

  const onSubmit = async (data) => {
    if (!draftOrderId) {
      showToast.error("order status ID is not available!");
      return;
    }
    if (!data.items || data.items.length === 0) {
      showToast.error("Please add at least one item before saving!");
      return;
    }

    try {
      const payload = {
        ...data,
        status: draftOrderId,
      };
      const response = await dispatch(AddNewOrder(payload));
      if (
        response?.payload?.statuscode === 201 ||
        response?.payload?.statuscode === 200
      ) {
        navigate("/orders/draft");

        reset();
      } else {
        console.error(
          "Create failed:",
          response?.payload?.msg || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Create failed:", error);
    }
  };

  const columns = React.useMemo(() => {
    if (orderType === ORDER_TYPE.CARRYING) {
      return [
        columnHelper.accessor("picture", {
          id: "image",
          header: "IMG",
          enableSorting: false,
          cell: ({ getValue }) => {
            const url = getValue();
            return url ? (
              <img
                src={url}
                alt="item"
                className="h-12 w-12 rounded object-cover ring-1 ring-gray-200"
                onError={(e) => (e.currentTarget.style.visibility = "hidden")}
              />
            ) : (
              <span className="text-gray-400">—</span>
            );
          },
        }),
        columnHelper.accessor("name", {
          id: "name",
          header: "NAME",
          enableSorting: false,
          cell: (info) => info.getValue() || "-",
        }),
        columnHelper.accessor("marka", {
          id: "marka",
          header: "MARKA",
          enableSorting: false,
          cell: (info) => info.getValue() || "-",
        }),
        columnHelper.accessor("ctn", {
          id: "ctn",
          header: "CTN",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("qty", {
          id: "qty",
          header: "QTY/CTN",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("totalQty", {
          id: "totalQty",
          header: "TOTAL QTY",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("rate", {
          id: "rate",
          header: "RATE",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("ttlAmt", {
          id: "ttlAmt",
          header: "TTL AMT",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("cbm", {
          id: "cbm",
          header: "CBM/CTN",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("ttlCbm", {
          id: "ttlCbm",
          header: "TTL CBM",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("kg", {
          id: "kg",
          header: "KG/CTN",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("ttlKgd", {
          id: "ttlKgd",
          header: "TTL KG",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("actions", {
          id: "actions",
          header: "ACTIONS",
          enableSorting: false,
          cell: ({ row }) => (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => remove(row.index)}
            >
              <FaTrashAlt className="h-4 w-4" />
            </Button>
          ),
        }),
      ];
    }

    // basedOnDone (default)
    return [
      columnHelper.accessor("picture", {
        id: "image",
        header: "IMG",
        enableSorting: false,
        cell: ({ getValue }) => {
          const url = getValue();
          return url ? (
            <img
              src={url}
              alt="item"
              className="h-12 w-12 rounded object-cover ring-1 ring-gray-200"
              onError={(e) => (e.currentTarget.style.visibility = "hidden")}
            />
          ) : (
            <span className="text-gray-400">—</span>
          );
        },
      }),
      columnHelper.accessor("name", {
        id: "name",
        header: "NAME",
        enableSorting: false,
        cell: (info) => info.getValue() || "Unnamed Item",
      }),
      columnHelper.accessor("description", {
        id: "description",
        header: "DESCRIPTION",
        enableSorting: false,
        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("totalQty", {
        id: "quantity",
        header: "QTY",
        enableSorting: false,
        cell: (info) => info.getValue() ?? "-",
      }),
      columnHelper.accessor("price", {
        id: "price",
        header: "PRICE",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("actions", {
        id: "actions",
        header: "ACTIONS",
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => remove(row.index)}
          >
            <FaTrashAlt className="h-4 w-4" />
          </Button>
        ),
      }),
    ];
  }, [orderType, remove]);

  return (
    <Card extra={"w-full h-full p-3"}>
      <header className="relative flex items-center justify-between">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Add Order
        </div>
      </header>

      <div className="mt-5">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="grid gap-4"
          noValidate
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Controller
              name="orderType"
              control={control}
              rules={{ required: "Order type is required" }}
              render={({ field }) => (
                <div className="flex flex-col">
                  <label className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white">
                    Order Type*
                  </label>
                  <div className="ml-1.5 mt-2 flex gap-6">
                    <label className="flex items-center gap-2">
                      <Radio
                        value={ORDER_TYPE.BASED_ON_DONE}
                        checked={field.value === ORDER_TYPE.BASED_ON_DONE}
                        onChange={(e) => field.onChange(e.target.value)}
                        name="orderType"
                        disabled={items?.length > 0}
                      />
                      <span>Base on Done</span>
                    </label>

                    <label className="flex items-center gap-2">
                      <Radio
                        value={ORDER_TYPE.CARRYING}
                        checked={field.value === ORDER_TYPE.CARRYING}
                        onChange={(e) => field.onChange(e.target.value)}
                        name="orderType"
                        disabled={items?.length > 0}
                      />
                      <span>Carrying</span>
                    </label>
                  </div>
                  {errors.orderType && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.orderType.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="shipmentType"
              control={control}
              rules={{ required: "Shipment type is required" }}
              render={({ field }) => (
                <div className="flex flex-col">
                  <label className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white">
                    Shipment Type*
                  </label>
                  <div className="ml-1.5 mt-2 flex gap-6">
                    <label className="flex items-center gap-2">
                      <Radio
                        value={SHIPMENT_TYPE.BY_AIR}
                        checked={field.value === SHIPMENT_TYPE.BY_AIR}
                        onChange={(e) => field.onChange(e.target.value)}
                        name="shipmentType"
                      />
                      <span>By Air</span>
                    </label>

                    <label className="flex items-center gap-2">
                      <Radio
                        value={SHIPMENT_TYPE.BY_SEA}
                        checked={field.value === SHIPMENT_TYPE.BY_SEA}
                        onChange={(e) => field.onChange(e.target.value)}
                        name="shipmentType"
                      />
                      <span>By Sea</span>
                    </label>
                  </div>
                  {errors.shipmentType && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.shipmentType.message}
                    </p>
                  )}
                </div>
              )}
            />
            {draft.selectUser && (
              <Controller
                name="orderFor"
                control={control}
                rules={{ required: "Order For is required" }}
                render={({ field }) => (
                  <SearchSelectField
                    label="Order For*"
                    options={orderForOptions}
                    placeholder="Search users..."
                    value={
                      orderForOptions.find(
                        (opt) => opt.value === field.value
                      ) || null
                    }
                    onChange={(selected) => {
                      field.onChange(selected?.value || null);

                      if (!selected) {
                        setSearchText("");
                      }
                    }}
                    onInputChange={(val) => setSearchText(val)}
                    isClearable
                    error={errors.orderFor}
                  />
                )}
              />
            )}
            <InputField
              id="description"
              variant="auth"
              label="Description"
              placeholder="Enter description"
              type="text"
              {...register("description")}
            />
          </div>

          {/* Items */}
          <div className="mt-3">
            <Button
              type="button"
              onClick={() => setIsItemModalOpen(true)}
              className="flex items-center gap-1"
            >
              <FiPlus />
              Add Item
            </Button>
          </div>

          {items.length > 0 && (
            <WithoutPageTable title="Items" columns={columns} data={items} />
          )}

          <div className="flex items-center justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => reset()}>
              Reset
            </Button>
            <LoaderButton type="submit" isLoading={createLoading}>
              Save as Draft
            </LoaderButton>
          </div>
        </form>
      </div>

      <AddItemModal
        isOpen={isItemModalOpen}
        onClose={() => setIsItemModalOpen(false)}
        onAddItem={(itemData) => append(itemData)}
        orderType={orderType} // 👈 pass the mode
      />
    </Card>
  );
}

export default CreateOrder;
